import { Button } from 'components/atoms';
import { Form } from 'components/organisms';
import { accountInitialValues } from 'components/organisms/forms/account/AccountForm';
import { FormikHelpers } from 'formik';
import { useMutateOptions } from 'hooks';
import { useParamId } from 'hooks/useParamId';
import middleware from 'middleware';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PiX } from 'react-icons/pi';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  useGetAccount,
  useSetAccountLicense,
  useUpdateAccount,
} from 'services/accounts';
import { IAccountMutationForm } from 'services/accounts/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';

const EditAccount = () => {
  const accountId = useParamId('account');
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const lastTab = pathname.split('/').pop();
  const backPathname = `/account/${accountId}/${lastTab}`;
  const { data: account, isLoading } = useGetAccount(accountId, false);
  const setAccountLicense = useSetAccountLicense();
  const updateAccount = useUpdateAccount();

  const mutateOptions = useMutateOptions<IAccountMutationForm>();

  const onUpdateAccount = async (
    values: IAccountMutationForm,
    helpers: FormikHelpers<IAccountMutationForm>
  ) => {
    const updatedLicence = middleware.account.formToLicenceQuery(values);
    const updatedAccount = middleware.account.formToAccountQuery(values);

    await updateAccount.mutateAsync(updatedAccount, {
      ...mutateOptions(helpers),
      onSuccess: () =>
        values.withLicense ? undefined : navigate(backPathname),
    });

    values.withLicense &&
      (await setAccountLicense.mutateAsync(
        {
          ...updatedLicence,
          license: values.withLicense ? updatedLicence.license : null,
        },
        {
          ...mutateOptions(helpers),
          onSuccess: () => navigate(backPathname),
        }
      ));
  };

  const [enableReinitialization, setEnableReinitialization] = useState(true);

  useEffect(() => {
    if (!account) return;

    const timer = setTimeout(() => {
      setEnableReinitialization(false);
    }, 100);

    return () => clearTimeout(timer);
  }, [account]);

  return (
    <Form.Accounts
      i18nextPrefix='account'
      enableReinitialize={enableReinitialization}
      initialValues={
        account ? middleware.account.queryToForm(account) : accountInitialValues
      }
      onSubmit={onUpdateAccount}
      customBackItem={
        <Button onlyIcon onClick={() => navigate(backPathname)}>
          <Button.Icon icon={<PiX />} />
        </Button>
      }
      topbarContent={
        <>
          <Button type='submit' variant={BUTTON_VARIANT.submit}>
            <Button.Text text='Save' />
          </Button>
        </>
      }
      status={{ isLoading }}
    />
  );
};

export default EditAccount;
